<template>
	<div class="flex flex-col">
		<div class="px-4 sm:px-6 lg:px-8 py-4">
			<div
				class="shadow border-b border-gray-200 relative"
				style="min-height: 81vh; max-height: 81vh; overflow-y: auto"
			>
						<div
							v-if="loading"
							class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-60 z-10"
						>
							<ui-loading-spinner :size="10" />
						</div>

						<table class="min-w-full divide-y divide-gray-200 relative">
							<thead class="bg-gray-700">
								<tr>
									<th
										scope="col"
										v-for="col in cols"
										:key="col.id"
										:class="[{ active: !!sortMap[col.id], 'z-10': openFilterColId === col.id }, col.thClass]"
										class="px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider sticky top-0 bg-gray-700 text-white"
									>
										<button
											v-if="!col.filterOptions && !col.filterType"
											@click.prevent="col.sortable !== false && $emit('sort-change', col.id)"
											class="btn inline-flex items-center uppercase font-semibold text-white"
											:class="{ 'text-gray-900 font-medium': !!sortMap[col.id] }"
										>
											<span>{{ col.name }}</span>

											<template v-if="col.sortable !== false">
												<span
													class="ml-2 inline-flex flex-col items-center"
													style="font-size: 0.7rem"
												>
													<font-awesome-icon
														:icon="['fas', 'caret-up']"
														:class="sortMap[col.id] && sortMap[col.id].dir === 'asc' ? 'opacity-100' : 'opacity-30'"
													/>
													<font-awesome-icon
														:icon="['fas', 'caret-down']"
														style="margin-top: -5px"
														:class="sortMap[col.id] && sortMap[col.id].dir === 'desc' ? 'opacity-100' : 'opacity-30'"
													/>
												</span>
												<span
													v-if="sortColumns.length > 1 && sortMap[col.id]"
													class="ml-1 text-xs leading-none opacity-75"
												>
													{{ sortMap[col.id].index }}
												</span>
											</template>
										</button>

										<div v-else-if="col.filterType === 'month'" class="flex items-center gap-x-2">
											<ui-month-input
												class="w-28"
												:value="filterValues[col.id] || null"
												:icon="false"
												:clearable="true"
												:chevron="true"
												size="sm"
												alignment="left"
												:placeholder="col.filterPlaceholder || null"
												@input="(val) => $emit('filter-change', { colId: col.id, value: val })"
												@opened="openFilterColId = col.id"
												@close="openFilterColId = null"
											/>

											<button
												v-if="col.sortable !== false"
												class="btn flex items-center text-white"
												@click.prevent="$emit('sort-change', col.id)"
											>
												<span class="inline-flex flex-col items-center" style="font-size: 0.7rem">
													<font-awesome-icon
														:icon="['fas', 'caret-up']"
														:class="sortMap[col.id] && sortMap[col.id].dir === 'asc' ? 'opacity-100' : 'opacity-30'"
													/>
													<font-awesome-icon
														:icon="['fas', 'caret-down']"
														style="margin-top: -5px"
														:class="sortMap[col.id] && sortMap[col.id].dir === 'desc' ? 'opacity-100' : 'opacity-30'"
													/>
												</span>
											</button>
										</div>

										<div v-else-if="col.filterType === 'date'" class="flex items-center gap-x-2">
											<!-- text-gray-700 am Wrapper: die Kalender-Zellen der Library setzen keine
											     eigene Farbe und erben sonst das text-white des dunklen Headers (weiß auf weiß).
											     relative: für den eigenen Clear-Button (unten) als Overlay im Input. -->
											<div class="relative text-gray-700 datepicker-filter">
												<datepicker
													:value="filterValues[col.id] || null"
													:placeholder="col.filterPlaceholder || null"
													input-class="text-gray-700 text-sm font-medium placeholder-gray-700 border border-gray-300 rounded-md pl-3 pr-8 py-2 bg-white w-32 cursor-pointer"
													format="dd.MM.yyyy"
													:typeable="false"
													@selected="(date) => $emit('filter-change', { colId: col.id, value: toIsoDate(date) })"
													@opened="openFilterColId = col.id"
													@closed="openFilterColId = null"
												></datepicker>

												<!-- Eigener Clear-Button statt des Library-Text-"×": identisch zum Monats-Filter
												     (FA times, h-3 w-3, gray-400/hover gray-600). -->
												<button
													v-if="filterValues[col.id]"
													type="button"
													class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
													@click.stop="$emit('filter-change', { colId: col.id, value: null })"
												>
													<font-awesome-icon :icon="['fas', 'times']" class="h-3 w-3" />
												</button>
												<font-awesome-icon
													v-else
													:icon="['fas', 'chevron-down']"
													class="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-700 pointer-events-none"
												/>
											</div>

											<button
												v-if="col.sortable !== false"
												class="btn flex items-center text-white"
												@click.prevent="$emit('sort-change', col.id)"
											>
												<span class="inline-flex flex-col items-center" style="font-size: 0.7rem">
													<font-awesome-icon
														:icon="['fas', 'caret-up']"
														:class="sortMap[col.id] && sortMap[col.id].dir === 'asc' ? 'opacity-100' : 'opacity-30'"
													/>
													<font-awesome-icon
														:icon="['fas', 'caret-down']"
														style="margin-top: -5px"
														:class="sortMap[col.id] && sortMap[col.id].dir === 'desc' ? 'opacity-100' : 'opacity-30'"
													/>
												</span>
											</button>
										</div>

										<div class="relative flex items-center gap-x-2" v-else>
											<ui-filter-dropdown
												:label="col.name"
												:options="col.filterOptions"
												:value="filterValues[col.id]"
												:multiple="col.filterMultiple === true"
												:dropdown-class="col.filterDropdownClass || undefined"
												@input="(val) => $emit('filter-change', { colId: col.id, value: val })"
												@opened="openFilterColId = col.id"
												@close="openFilterColId = null"
											/>
											<button
												v-if="col.sortable === true"
												class="btn flex items-center text-white"
												@click.prevent="$emit('sort-change', col.id)"
											>
												<span class="inline-flex flex-col items-center" style="font-size: 0.7rem">
													<font-awesome-icon
														:icon="['fas', 'caret-up']"
														:class="sortMap[col.id] && sortMap[col.id].dir === 'asc' ? 'opacity-100' : 'opacity-30'"
													/>
													<font-awesome-icon
														:icon="['fas', 'caret-down']"
														style="margin-top: -5px"
														:class="sortMap[col.id] && sortMap[col.id].dir === 'desc' ? 'opacity-100' : 'opacity-30'"
													/>
												</span>
											</button>
										</div>
									</th>
									<th
										v-if="$scopedSlots['row-actions']"
										class="px-4 py-3 sticky top-0 bg-gray-700"
									></th>
								</tr>
							</thead>
							<tbody class="bg-white divide-y divide-gray-200">
								<tr
									v-for="(row, idx) in renderedRows"
									:key="idx"
									class="hover:bg-gray-50"
									:class="{ 'cursor-pointer': !!rowHref }"
									@click="onRowClick(row.original)"
								>
									<td
										v-for="col in cols"
										:key="col.id"
										:class="col.tdClass"
										class="px-4 py-4 whitespace-nowrap"
									>
										<slot :name="`cell-${col.id}`" :row="row.original" :value="row.rendered[col.id]">
											<span v-if="row.rendered[col.id] == null">
												<i>{{ __("n/a") }}</i>
											</span>
											<span v-else>{{ row.rendered[col.id] }}</span>
										</slot>
									</td>
									<td
										v-if="$scopedSlots['row-actions']"
										class="px-2 py-1 whitespace-nowrap text-right"
										@click.stop
									>
										<slot name="row-actions" :row="row.original"></slot>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
		</div>

		<slot name="pagination">
			<div class="sticky bottom-0 bg-white border-t border-gray-200 p-4 z-10" v-if="pagination && pagination.lastPage > 1">
				<div class="flex justify-between items-center pt-2 pb-2">
					<span>
						<ui-button
							v-if="pagination.currentPage > 1"
							label="Back"
							@click="$emit('page-change', pagination.currentPage - 1)"
						/>
					</span>
					<span class="text-sm text-gray-600">
						{{ __("Seite") }} {{ pagination.currentPage }} / {{ pagination.lastPage }}
						&nbsp;({{ pagination.total }} {{ __("Einträge") }})
					</span>
					<span>
						<ui-button
							v-if="pagination.currentPage < pagination.lastPage"
							label="Next"
							@click="$emit('page-change', pagination.currentPage + 1)"
						/>
					</span>
				</div>
			</div>
		</slot>
	</div>
</template>

<script>
/**
 * Admin/Table — generische, servergesteuerte Tabelle für die Admin-Grids
 * (löst schrittweise das alte CropGrid ab; aktiv u.a. in LocalPricesGrid.vue).
 *
 * Bewusst "dumm": Rendering, Sortierung und Filterung sind rein datengetrieben
 * über `cols` konfiguriert. Den Zustand (Sortierung/Filter/Seite) hält der
 * PARENT und reicht ihn per Props herein; die Tabelle emittiert nur Intents
 * (sort-change / filter-change / page-change) und rendert, was der Parent an
 * `rows` zurückgibt. Die eigentlichen Requests laufen also im Parent.
 *
 * Spalten-Contract (`cols` = Array von Objekten):
 *   id                  {string}   Pflicht. Key in der Row (row[id]) und für Slot/Sort/Filter.
 *   name                {string}   Header-Beschriftung.
 *   render              {Function} Optional. (value, row) => Anzeigewert. Fehlt → row[id] roh.
 *   sortable            {boolean}  Optional. false = keine Sortier-Pfeile / kein sort-change. Bei filterOptions-Spalten ist der Sort-Caret opt-in: sortable: true blendet ihn zusaetzlich zum Dropdown ein.
 *   thClass             {string}   Optional. Zusatz-Klassen am Header-<th> (z.B. 'w-full text-right').
 *   tdClass             {string}   Optional. Zusatz-Klassen an der Zell-<td> (z.B. 'text-right max-w-xs truncate').
 *   filterType          {string}   Optional. 'month' | 'date' → eigene Filter-Eingabe im Header.
 *   filterOptions       {Array}    Optional. [{id, name}] → Dropdown-Filter (Radios + "kein Filter").
 *   filterMultiple      {boolean}  Optional. true (nur mit filterOptions) → Checkbox-Mehrfachauswahl; filter-change value ist ein Array von ids (leer = kein Filter).
 *   filterPlaceholder   {string}   Optional. Platzhalter des Monats-Filters.
 *   filterDropdownClass {string}   Optional. Positionierungs-Override des Dropdowns.
 *   (ohne filterType und filterOptions → schlichter, sortierbarer Header)
 *
 * Events an den Parent:
 *   sort-change   (colId)              Klick auf einen sortierbaren Header.
 *   filter-change ({ colId, value })   Filter gesetzt/geändert; value === null = zurückgesetzt.
 *   page-change   (page)               Blättern (aus dem Default-Pagination-Slot).
 *
 * Slots:
 *   cell-<colId>  ({ row, value })   Zell-Override je Spalte (row = Original-Row, value = gerendert).
 *   row-actions   ({ row })          Aktionsspalte rechts (nur gerendert, wenn Slot vorhanden).
 *   pagination                       Ersetzt die Default-Paginierung.
 */
import Datepicker from "vuejs-datepicker";

export default {
	components: {
		Datepicker,
	},

	props: {
		// Roh-Daten der aktuellen Seite; pro Zeile liefert col.id den Wert (row[col.id]).
		rows: {
			type: Array,
			required: true,
		},
		// Spalten-Definition inkl. Render-/Sort-/Filter-Verhalten — Contract siehe Kopf-Kommentar.
		cols: {
			type: Array,
			required: true,
		},
		// Blendet den Overlay-Spinner ein, während im Parent ein Request läuft.
		loading: {
			type: Boolean,
			default: false,
		},
		// Aktive Sortierung (Multi-Sort); Array-Reihenfolge = Priorität: [{ column, dir: 'asc'|'desc' }].
		sortColumns: {
			type: Array,
			default: () => [],
		},
		// Aktive Filter je Spalte: { colId: value }. Quelle der Wahrheit bleibt der Parent.
		filterValues: {
			type: Object,
			default: () => ({}),
		},
		// Paginierungs-Meta { currentPage, lastPage, total }; null = keine Paginierung.
		pagination: {
			type: Object,
			default: null,
		},
		// Optional: (row) => URL. Gesetzt → ganze Zeile ist klickbar und navigiert dorthin.
		rowHref: {
			type: Function,
			default: null,
		},
	},
	data() {
		return {
			// Spalte, deren Filter-Dropdown gerade offen ist — hebt deren Header per z-Index an.
			openFilterColId: null,
		};
	},

	computed: {
		/**
		 * Baut eine Map { colId: { dir, index } } aus sortColumns für schnellen Zugriff im Template.
		 *
		 * @returns {Object}
		 */
		sortMap() {
			const map = {};

			this.sortColumns.forEach((s, idx) => {
				map[s.column] = { dir: s.dir, index: idx + 1 };
			});

			return map;
		},

		/**
		 * Wendet col.render auf jede Zeile an, behält Original-Row für den row-actions-Slot.
		 *
		 * @returns {Array}
		 */
		renderedRows() {
			return this.rows.map((row) => {
				const rendered = {};

				this.cols.forEach((col) => {
					rendered[col.id] = col.render ? col.render(row[col.id], row) : row[col.id];
				});

				return { rendered, original: row };
			});
		},
	},
	methods: {
		/**
		 * Wandelt das vom Datepicker gelieferte Date in einen ISO-Datumsstring (YYYY-MM-DD)
		 * für den filter-change-Intent um. Lokale Datumsteile (kein UTC), damit der angezeigte
		 * Tag und der gesendete Wert übereinstimmen. null bei leerer Auswahl.
		 *
		 * @param {Date|null} date
		 * @returns {string|null}
		 */
		toIsoDate(date) {
			if (!date) {
				return null;
			}

			const d   = new Date(date);
			const pad = (n) => String(n).padStart(2, "0");

			return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
		},

		/**
		 * Navigiert zur rowHref-Ziel-URL, wenn die Prop gesetzt ist.
		 *
		 * @param {Object} row Original-Row.
		 * @returns {void}
		 */
		onRowClick(row) {
			if (!this.rowHref) {
				return;
			}

			const href = this.rowHref(row);

			if (href) {
				this.$inertia.get(href);
			}
		},
	},
};
</script>

<style scoped>
/* Kalender-Popup wie die anderen Filter-Dropdowns: Abstand (mt-2) und runde Ecken (rounded-md). */
.datepicker-filter ::v-deep .vdp-datepicker__calendar {
	margin-top: 0.5rem;
	border-radius: 0.375rem;
	overflow: hidden;
}
</style>
